/**
 * TaskListTable — Tabela completa de tarefas (header + linhas + subtarefas
 * recursivas até 3 níveis), reutilizada tanto pela visualização padrão
 * (TaskList) quanto por cada grupo da visualização agrupada
 * (TaskGroupedView).
 *
 * Toda a lógica de estado e handlers permanece em TaskList; o componente
 * consome via `TaskListTableContext` (Provider envolve ambos os caminhos
 * de render). Recebe apenas a lista de tarefas a renderizar.
 *
 * @version 1.2.0
 * RELEASE: 2026-08-22 v1.2.0 - Nova coluna "Data de entrada" no módulo Jurídico

 * RELEASE: 2026-08-22 v1.1.0 - Coluna "Período" também no Jurídico (data de
 *          entrada), Status reposicionado logo após Período e nova coluna
 *          "Motivo conclusão" nos módulos Retenção/Jurídico.
 * RELEASE: 2026-06-24 v1.0.0 - Extração da Table original de TaskList.tsx
 *          para reaproveitamento na visão agrupada (mesma estrutura,
 *          mesmas colunas, mesmos componentes, scroll horizontal próprio).
 */
import { createContext, useContext, Fragment } from 'react';
import { useTaskModuleType } from '@/hooks/useTaskModuleType';
import { useRetentionPeriodsMeta, type RetentionTaskMeta } from '@/hooks/useRetentionPeriodsMeta';
import { RetentionPeriodBadge } from '@/components/tasks/RetentionPeriodBadge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertTriangle,
  ArrowUpDown,
  Calendar as CalendarIcon,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Eye,
  Filter,
  GitBranch,
  ListChecks,
  MessageSquare,
  PauseCircle,
  Pencil,
  PlayCircle,
  RotateCcw,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { PriorityFlag, PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { TaskTimerCell } from '@/components/tasks/TaskTimerCell';
import { getDeadlineInfo, DEADLINE_PILL_STYLES } from '@/lib/deadline-utils';
import type { TaskListItem } from '@/hooks/useTasksPaginated';
import {
  PhaseDropdown,
  PRIORITY_BAR_COLORS,
  STATUS_LABELS,
  STATUS_STYLES,
  formatDue,
} from './taskListShared';

/* =========================================================================
 * Contexto compartilhado — handlers e estado vivem em TaskList; a Table
 * apenas consome. Evita prop-drilling pesado entre TaskList ↔ Grouped.
 * ========================================================================= */
export interface TaskListTableContextValue {
  /** Modo de seleção múltipla ativo. */
  bulkEditMode: boolean;
  /** IDs atualmente selecionados. */
  selectedTaskIds: Set<string>;
  /** Liga/desliga seleção de uma tarefa. */
  toggleTaskSelection: (taskId: string) => void;
  /** Atualiza diretamente o set de selecionados (usado pelo "selecionar todos"). */
  setSelectedTaskIds: React.Dispatch<React.SetStateAction<Set<string>>>;

  /** Ordenação atual. */
  sortBy: string;
  sortDir: string;
  /** Handler que alterna ordenação de um campo. */
  handleSort: (field: string) => void;

  /** Estado de expand/collapse e cache de subtarefas. */
  expandedTasks: Set<string>;
  loadingSubtasks: Set<string>;
  subtasksMap: Record<string, TaskListItem[]>;
  toggleExpandTask: (taskId: string, e: React.MouseEvent) => void;

  /** Permissões granulares por fase. */
  canDoOnPhase: (
    phaseId: string,
    action:
      | 'can_view'
      | 'can_create'
      | 'can_edit'
      | 'can_delete'
      | 'can_change_phase'
      | 'can_change_status'
      | 'can_reopen',
    isResponsible: boolean
  ) => boolean;
  isResponsibleFor: (task: TaskListItem) => boolean;

  /** Handlers de UI. */
  handleViewTask: (task: TaskListItem) => void;
  handleEditTask: (task: TaskListItem) => void;
  handleChangeStatus: (taskId: string, newStatus: string) => void | Promise<void>;
  /** Abre o diálogo "Alterar Fase" (troca de categoria/fase) da tarefa. */
  handleChangePhase: (task: TaskListItem) => void;
}

const TaskListTableContext = createContext<TaskListTableContextValue | null>(null);

/* -------------------------------------------------------------------------
 * Contexto auxiliar — metadados de "Período" das solicitações de Retenção.
 * Buscados em lote pelo componente <TaskListTable> (uma chamada por página)
 * e consumidos pelas linhas, evitando N+1.
 * ----------------------------------------------------------------------- */
const RetentionPeriodContext = createContext<Record<string, RetentionTaskMeta>>({});

/** Provider — envolve TaskList e TaskGroupedView. */
export function TaskListTableProvider({
  value,
  children,
}: {
  value: TaskListTableContextValue;
  children: React.ReactNode;
}) {
  return (
    <TaskListTableContext.Provider value={value}>
      {children}
    </TaskListTableContext.Provider>
  );
}

/** Hook interno — falha cedo se o componente for usado fora do Provider. */
function useTaskListTableContext(): TaskListTableContextValue {
  const ctx = useContext(TaskListTableContext);
  if (!ctx) {
    throw new Error(
      'TaskListTable deve ser renderizado dentro de <TaskListTableProvider>'
    );
  }
  return ctx;
}

/* =========================================================================
 * Helpers locais de renderização
 * ========================================================================= */

/** Formata "Criado em" (dd/MM/yy HH:mm). */
const formatCreatedAt = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return format(new Date(dateStr), 'dd/MM/yy HH:mm', { locale: ptBR });
};

/** Ícone de ordenação contextual à coluna. */
function SortIcon({
  active,
  direction,
}: {
  active: boolean;
  direction: string;
}) {
  if (active) {
    return direction === 'asc' ? (
      <ChevronUp className="h-3 w-3" />
    ) : (
      <ChevronDown className="h-3 w-3" />
    );
  }
  return <ArrowUpDown className="h-3 w-3 opacity-30" />;
}

/* =========================================================================
 * Header da tabela (12 colunas) — reutilizado por todos os caminhos
 * ========================================================================= */
interface TaskListTableHeaderProps {
  tasks: TaskListItem[];
}

function TaskListTableHeader({ tasks }: TaskListTableHeaderProps) {
  // Coluna "Período" existe em Retenção (data de solicitação) e Jurídico (data de entrada)
  const taskModule = useTaskModuleType();
  const hasPeriodColumn = taskModule === 'retention' || taskModule === 'legal';
  const {
    bulkEditMode,
    selectedTaskIds,
    setSelectedTaskIds,
    sortBy,
    sortDir,
    handleSort,
  } = useTaskListTableContext();

  /** Liga/desliga seleção de todas as tarefas visíveis nesta tabela. */
  const allSelected =
    tasks.length > 0 && tasks.every((t) => selectedTaskIds.has(t.id));
  const toggleAll = () => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        // remove apenas as tasks desta tabela (preserva seleções de outros grupos)
        tasks.forEach((t) => next.delete(t.id));
      } else {
        tasks.forEach((t) => next.add(t.id));
      }
      return next;
    });
  };

  return (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        {bulkEditMode && (
          <TableHead className="w-10 px-2">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleAll}
              aria-label="Selecionar todas"
            />
          </TableHead>
        )}
        {/* Barra de prioridade — sem rótulo */}
        <TableHead className="w-8 px-2" />
        {/* Nº */}
        <TableHead
          className={cn(
            'text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none',
            // Retenção precisa de mais largura p/ o badge do protocolo em linha única
            taskModule === 'retention' ? 'w-[150px]' : 'w-16'
          )}
          onClick={() => handleSort('number')}
        >
          <div className="flex items-center gap-1 whitespace-nowrap">
            {/* Retenção exibe "N. Protocolo" no lugar de "Nº" — v2.3.0 */}
            <span>{taskModule === 'retention' ? 'N. Protocolo' : 'Nº'}</span>
            <SortIcon active={sortBy === 'number'} direction={sortDir} />
          </div>
        </TableHead>
        {/* Título */}
        <TableHead
          className="text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
          onClick={() => handleSort('title')}
        >
          <div className="flex items-center gap-1">
            <span>Título</span>
            <SortIcon active={sortBy === 'title'} direction={sortDir} />
          </div>
        </TableHead>
        {/* Cliente — padrão unificado (avatar + nome) v2.4.0 */}
        <TableHead className="w-40 text-xs font-medium">Cliente</TableHead>
        {/* Data de entrada (exclusivo Jurídico) — v1.2.0 */}
        {taskModule === 'legal' && (
          <TableHead className="w-28 text-xs font-medium">Data de entrada</TableHead>
        )}
        {/* Período + Status + Motivo (Retenção/Jurídico) */}
        {hasPeriodColumn && (
          <>
            <TableHead className="w-24 text-xs font-medium text-center">Período</TableHead>

            <TableHead
              className="w-28 text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
              onClick={() => handleSort('status')}
            >
              <div className="flex items-center gap-1">
                <span>Status</span>
                <SortIcon active={sortBy === 'status'} direction={sortDir} />
              </div>
            </TableHead>
            <TableHead className="w-36 text-xs font-medium">Motivo conclusão</TableHead>
          </>
        )}
        {/* Info */}
        <TableHead className="w-20 text-xs font-medium text-center">Info</TableHead>
        {/* Checklist */}
        <TableHead className="w-16 text-xs font-medium text-center">
          <ListChecks className="h-3 w-3 mx-auto" />
        </TableHead>
        {/* Cronômetro */}
        <TableHead className="w-28 text-xs font-medium text-center">
          <div className="flex items-center justify-center gap-1">
            <Clock className="h-3 w-3" />
            <span>Cronômetro</span>
          </div>
        </TableHead>
        {/* Categoria */}
        <TableHead
          className="w-28 text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
          onClick={() => handleSort('category_name')}
        >
          <div className="flex items-center gap-1">
            <span>Categoria</span>
            <SortIcon active={sortBy === 'category_name'} direction={sortDir} />
          </div>
        </TableHead>
        {/* Fase */}
        <TableHead
          className="w-32 text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
          onClick={() => handleSort('phase_name')}
        >
          <div className="flex items-center gap-1">
            <span>Fase</span>
            <SortIcon active={sortBy === 'phase_name'} direction={sortDir} />
          </div>
        </TableHead>
        {/* Prioridade */}
        <TableHead
          className="w-24 text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
          onClick={() => handleSort('priority')}
        >
          <div className="flex items-center gap-1">
            <span>Prioridade</span>
            <SortIcon active={sortBy === 'priority'} direction={sortDir} />
          </div>
        </TableHead>
        {/* Status (posição padrão — nos módulos com Período fica após ele) */}
        {!hasPeriodColumn && (
          <TableHead
            className="w-28 text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
            onClick={() => handleSort('status')}
          >
            <div className="flex items-center gap-1">
              <span>Status</span>
              <SortIcon active={sortBy === 'status'} direction={sortDir} />
            </div>
          </TableHead>
        )}
        {/* Responsável */}
        <TableHead className="w-36 text-xs font-medium">Responsável</TableHead>
        {/* Prazo */}
        <TableHead
          className="w-44 text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
          onClick={() => handleSort('due_date')}
        >
          <div className="flex items-center gap-1">
            <span>Prazo</span>
            <SortIcon active={sortBy === 'due_date'} direction={sortDir} />
          </div>
        </TableHead>
        {/* Criado em */}
        <TableHead
          className="w-32 text-xs font-medium cursor-pointer hover:bg-muted/30 transition-colors select-none"
          onClick={() => handleSort('created_at')}
        >
          <div className="flex items-center gap-1">
            <span>Criado em</span>
            <SortIcon active={sortBy === 'created_at'} direction={sortDir} />
          </div>
        </TableHead>
        {/* Data conclusão (v1.2.0) — presente nos três módulos (sem ordenação server-side) */}
        <TableHead className="w-32 text-xs font-medium">Data conclusão</TableHead>
        {/* Ações */}
        <TableHead className="w-10" />
      </TableRow>
    </TableHeader>
  );
}

/* =========================================================================
 * Pill de prazo (badge "Vencido" / "Restam")
 * ========================================================================= */
function DeadlinePill({
  dueDate,
  status,
}: {
  dueDate: string | null;
  status: string;
}) {
  const info = getDeadlineInfo(dueDate, status);
  if (!info) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-px rounded-full px-1 py-px text-[10px] font-medium whitespace-nowrap',
        DEADLINE_PILL_STYLES[info.variant]
      )}
    >
      {info.variant === 'overdue' ? (
        <AlertTriangle className="h-3 w-3" />
      ) : (
        <Clock className="h-3 w-3" />
      )}
      {info.text}
    </span>
  );
}

/* =========================================================================
 * Linha de tarefa (pai ou filha) — densidade ajustada por nível
 * ========================================================================= */
interface TaskRowProps {
  task: TaskListItem;
  /** Nível de indentação (0=pai, 1=filha, 2=neta). */
  depth?: 0 | 1 | 2;
}

function TaskRow({ task, depth = 0 }: TaskRowProps) {
  const ctx = useTaskListTableContext();
  const {
    bulkEditMode,
    selectedTaskIds,
    toggleTaskSelection,
    expandedTasks,
    loadingSubtasks,
    subtasksMap,
    toggleExpandTask,
    canDoOnPhase,
    isResponsibleFor,
    handleViewTask,
    handleEditTask,
    handleChangeStatus,
    handleChangePhase,
  } = ctx;

  // Período (Retenção/Jurídico) — metadados vindos do contexto em lote
  const taskModule = useTaskModuleType();
  const hasPeriodColumn = taskModule === 'retention' || taskModule === 'legal';
  const periodMeta = useContext(RetentionPeriodContext)[task.id];

  /** Badge de status reutilizado nas duas posições possíveis da coluna */
  const statusBadge = (
    <Badge
      className={cn(
        'text-[10px] h-5 px-1.5 whitespace-nowrap',
        STATUS_STYLES[task.status]
      )}
    >
      {STATUS_LABELS[task.status] || task.status}
    </Badge>
  );

  const isParent = depth === 0;
  const cellPadY = isParent ? 'py-2' : 'py-1.5';
  const checkboxPadY = isParent ? 'py-2' : 'py-1.5';
  const priorityBarH = isParent ? 'h-8' : 'h-6';
  const titleTextClass = isParent ? 'text-sm font-medium' : 'text-sm';
  const assigneeTextClass = isParent
    ? 'text-xs text-foreground truncate max-w-[80px]'
    : 'text-xs text-muted-foreground truncate max-w-[80px]';
  const subPriorityTextClass = isParent ? 'text-foreground' : 'text-muted-foreground';

  // Indentação visual da coluna Nº
  const numberIndent =
    depth === 0 ? '' : depth === 1 ? 'pl-5' : 'pl-10';
  const numberPrefix =
    depth === 0 ? null : depth === 1 ? (
      <span className="text-muted-foreground/40">└</span>
    ) : (
      <span className="text-muted-foreground/30">└└</span>
    );
  const titleIndent = depth === 0 ? '' : depth === 1 ? 'pl-2' : 'pl-4';

  return (
    <TableRow
      className={cn(
        'cursor-pointer hover:bg-muted/40 group',
        depth === 1 && 'bg-muted/20',
        depth === 2 && 'bg-muted/30',
        bulkEditMode && selectedTaskIds.has(task.id) && 'bg-primary/5'
      )}
      onClick={() => {
        if (bulkEditMode) {
          toggleTaskSelection(task.id);
        } else {
          handleViewTask(task);
        }
      }}
    >
      {/* Checkbox bulk edit */}
      {bulkEditMode && (
        <TableCell className={cn('px-2', checkboxPadY)} onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selectedTaskIds.has(task.id)}
            onCheckedChange={() => toggleTaskSelection(task.id)}
            aria-label={`Selecionar tarefa ${task.number}`}
          />
        </TableCell>
      )}

      {/* Barra de prioridade */}
      <TableCell className={cn('px-2', cellPadY)}>
        <div
          className={cn(
            'w-1 rounded-full',
            priorityBarH,
            PRIORITY_BAR_COLORS[task.priority] || 'bg-muted-foreground/30'
          )}
        />
      </TableCell>

      {/* Número + expand */}
      <TableCell
        className={cn(
          'font-mono text-xs text-muted-foreground',
          cellPadY,
          task.subtasks_count > 0 &&
            depth < 2 &&
            'cursor-pointer select-none hover:bg-muted/40 transition-colors'
        )}
        onClick={
          task.subtasks_count > 0 && depth < 2
            ? (e) => toggleExpandTask(task.id, e)
            : undefined
        }
      >
        <div className={cn('flex items-center gap-0.5', numberIndent || 'gap-1')}>
          {numberPrefix}
          {task.subtasks_count > 0 && depth < 2 ? (
            <ChevronRight
              className={cn(
                'shrink-0 transition-transform duration-200',
                depth === 0 ? 'h-4 w-4' : 'h-3.5 w-3.5',
                expandedTasks.has(task.id) && 'rotate-90',
                loadingSubtasks.has(task.id) && 'opacity-50'
              )}
            />
          ) : (
            <span className={cn('shrink-0', depth === 0 ? 'w-4' : 'w-3.5')} />
          )}
          {/* Retenção: protocolo em badge compacto e sem quebra de linha — v2.4.0 */}
          {taskModule === 'retention' && periodMeta?.protocol_number ? (
            <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground whitespace-nowrap">
              {periodMeta.protocol_number}
            </span>
          ) : (
            `#${task.number}`
          )}
        </div>
      </TableCell>

      {/* Título */}
      <TableCell
        className={cn(
          cellPadY,
          'max-w-[210px] sm:max-w-[280px] lg:max-w-[350px]'
        )}
      >
        <div className={cn('min-w-0', titleIndent)}>
          <p className={cn(titleTextClass, 'truncate')}>{task.title}</p>
          {isParent && task.parent_task_id && (
            <p className="text-[10px] text-muted-foreground/60 italic truncate">
              ↳ Subtarefa
            </p>
          )}
          {task.classification_name && (!isParent || !task.parent_task_id) && (
            <p className="text-xs text-muted-foreground truncate">
              {task.classification_name}
            </p>
          )}
        </div>
      </TableCell>

      {/* Cliente — padrão unificado: avatar (iniciais) + nome truncado — v2.4.0 */}
      <TableCell className={cellPadY}>
        {task.contact_name ? (
          <div className="flex items-center gap-2 min-w-0">
            <SmartAvatar src={task.contact_avatar ?? undefined} name={task.contact_name} size="xs" />
            <span className="text-sm truncate max-w-[120px]">{task.contact_name}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>

      {/* Data de entrada (exclusivo Jurídico) — v1.2.0 */}
      {taskModule === 'legal' && (
        <TableCell className={cellPadY}>
          {periodMeta?.requested_at ? (
            <span className="text-xs whitespace-nowrap">
              {format(new Date(`${periodMeta.requested_at.slice(0, 10)}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR })}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/40">-</span>
          )}
        </TableCell>
      )}

      {/* Período + Status + Motivo conclusão (Retenção/Jurídico) */}

      {hasPeriodColumn && (
        <>
          <TableCell className={cn('text-center', cellPadY)}>
            {periodMeta?.requested_at ? (
              <RetentionPeriodBadge
                requestedAt={periodMeta.requested_at}
                closedAt={periodMeta.completed_at || periodMeta.cancelled_at}
              />
            ) : (
              <span className="text-xs text-muted-foreground/40">-</span>
            )}
          </TableCell>
          <TableCell className={cellPadY}>{statusBadge}</TableCell>
          <TableCell className={cellPadY}>
            {periodMeta?.completion_reason_name ? (
              <Badge
                variant="outline"
                className="text-[10px] h-5 px-1.5 max-w-[130px]"
              >
                <span
                  className="w-2 h-2 rounded-full mr-1 shrink-0"
                  style={{
                    backgroundColor:
                      periodMeta.completion_reason_color || 'hsl(var(--muted-foreground))',
                  }}
                />
                <span className="truncate">{periodMeta.completion_reason_name}</span>
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground/40">-</span>
            )}
          </TableCell>
        </>
      )}

      {/* Info (subtarefas + comentários) */}
      <TableCell className={cn('text-center', cellPadY)}>
        <div className="flex items-center justify-center gap-2">
          {task.subtasks_count > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <ListChecks className="h-3 w-3" />
              {task.subtasks_count}
            </span>
          )}
          {task.comments_count > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
              <MessageSquare className="h-3 w-3" />
              {task.comments_count}
            </span>
          )}
        </div>
      </TableCell>

      {/* Checklist (%) */}
      <TableCell className={cn('text-center', cellPadY)}>
        {(() => {
          const pct = task.checklist_completion_pct ?? 0;
          if (pct === 0) {
            return <span className="text-xs text-muted-foreground/40">-</span>;
          }
          const colorClass =
            pct >= 70
              ? 'text-emerald-600 dark:text-emerald-400'
              : pct >= 40
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-red-600 dark:text-red-400';
          return <span className={cn('text-xs font-medium', colorClass)}>{pct}%</span>;
        })()}
      </TableCell>

      {/* Cronômetro */}
      <TableCell className={cn('text-center', cellPadY)}>
        <TaskTimerCell
          taskId={task.id}
          status={task.status}
          startedAt={task.started_at}
          isPaused={task.is_paused}
          totalPauseDurationSeconds={task.total_pause_duration_seconds}
          currentPauseStartedAt={task.current_pause_started_at}
          canChangeStatus={canDoOnPhase(
            task.phase_id,
            'can_change_status',
            isResponsibleFor(task)
          )}
          completedAt={(task as any).completed_at ?? null}
          cancelledAt={(task as any).cancelled_at ?? null}
          dueDate={task.due_date}
        />
      </TableCell>

      {/* Categoria */}
      <TableCell className={cellPadY}>
        <Badge
          variant="outline"
          className="text-[10px] h-5 px-1.5 max-w-[100px]"
        >
          <span
            className="w-2 h-2 rounded-full mr-1 shrink-0"
            style={{ backgroundColor: task.category_color }}
          />
          <span className="truncate">{task.category_name}</span>
        </Badge>
      </TableCell>

      {/* Fase */}
      <TableCell className={cellPadY}>
        {depth < 2 ? (
          <PhaseDropdown
            task={task}
            disabled={
              !canDoOnPhase(
                task.phase_id,
                'can_change_phase',
                isResponsibleFor(task)
              )
            }
            // v2: lista apenas fases de destino permitidas (todas as categorias)
            canMoveToPhase={(phaseId) =>
              canDoOnPhase(phaseId, 'can_change_phase', isResponsibleFor(task))
            }
          />
        ) : (
          // Nível 3 mantém o comportamento original: dropdown habilitado por
          // permissão, mesmo render.
          <PhaseDropdown
            task={task}
            disabled={
              !canDoOnPhase(
                task.phase_id,
                'can_change_phase',
                isResponsibleFor(task)
              )
            }
            // v2: lista apenas fases de destino permitidas (todas as categorias)
            canMoveToPhase={(phaseId) =>
              canDoOnPhase(phaseId, 'can_change_phase', isResponsibleFor(task))
            }
          />
        )}
      </TableCell>

      {/* Prioridade */}
      <TableCell className={cellPadY}>
        <div className="flex items-center gap-1.5">
          <PriorityFlag priority={task.priority} size={12} />
          <span className={cn('text-xs', subPriorityTextClass)}>
            {PRIORITY_LABELS[task.priority] || task.priority}
          </span>
        </div>
      </TableCell>

      {/* Status (posição padrão — nos módulos com Período fica após ele) */}
      {!hasPeriodColumn && <TableCell className={cellPadY}>{statusBadge}</TableCell>}

      {/* Responsável */}
      <TableCell className={cellPadY}>
        {task.assigned_to_name ? (
          <div className="flex items-center gap-2">
            <SmartAvatar
              src={task.assigned_to_avatar}
              name={task.assigned_to_name}
              size="xs"
              use3DFallback
            />
            <span className={assigneeTextClass}>{task.assigned_to_name}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>

      {/* Prazo */}
      <TableCell className={cellPadY}>
        <div className="flex flex-col gap-1 items-start min-w-0">
          <div className="flex items-center gap-1 text-xs text-foreground">
            <CalendarIcon className="h-3 w-3 text-muted-foreground" />
            {formatDue(task.due_date)}
          </div>
          <DeadlinePill dueDate={task.due_date} status={task.status} />
        </div>
      </TableCell>

      {/* Criado em */}
      <TableCell className={cellPadY}>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatCreatedAt(task.created_at)}
        </span>
      </TableCell>

      {/* Data conclusão (v1.2.0) — vazia quando a tarefa ainda não foi concluída */}
      <TableCell className={cellPadY}>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {(task as any).completed_at ? formatCreatedAt((task as any).completed_at) : '-'}
        </span>
      </TableCell>


      {/* Menu de Ações */}
      <TableCell className={cellPadY} onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Filter className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-52 p-1.5 shadow-lg border-border/50"
          >
            <DropdownMenuItem
              onClick={() => handleViewTask(task)}
              className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
            >
              <Eye className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Ver Detalhes</span>
            </DropdownMenuItem>
            {canDoOnPhase(task.phase_id, 'can_edit', isResponsibleFor(task)) && (
              <DropdownMenuItem
                onClick={() => handleEditTask(task)}
                className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
              >
                <Pencil className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Editar</span>
              </DropdownMenuItem>
            )}
            {/* Atalho "Alterar Fase" (categoria/fase) — mesmo diálogo do modal de detalhes */}
            {canDoOnPhase(task.phase_id, 'can_change_phase', isResponsibleFor(task)) && (
              <DropdownMenuItem
                onClick={() => handleChangePhase(task)}
                className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
              >
                <GitBranch className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Alterar Fase</span>
              </DropdownMenuItem>
            )}

            {/* Ações de status só fazem sentido para pais e filhas — para
                manter paridade com o comportamento legado, oferecemos os
                mesmos atalhos em qualquer profundidade onde haja permissão. */}
            {depth < 2 && (
              <>
                <DropdownMenuSeparator className="my-1.5" />
                {task.status === 'pending' &&
                  canDoOnPhase(
                    task.phase_id,
                    'can_change_status',
                    isResponsibleFor(task)
                  ) && (
                    <DropdownMenuItem
                      onClick={() => handleChangeStatus(task.id, 'in_progress')}
                      className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-green-600 dark:text-green-400 focus:text-green-600 focus:bg-green-50 dark:focus:bg-green-950/30"
                    >
                      <PlayCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">Iniciar Tarefa</span>
                    </DropdownMenuItem>
                  )}
                {task.status === 'in_progress' &&
                  canDoOnPhase(
                    task.phase_id,
                    'can_change_status',
                    isResponsibleFor(task)
                  ) && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleChangeStatus(task.id, 'paused')}
                        className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-yellow-600 dark:text-yellow-400 focus:text-yellow-600 focus:bg-yellow-50 dark:focus:bg-yellow-950/30"
                      >
                        <PauseCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">Pausar</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleChangeStatus(task.id, 'completed')}
                        className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-emerald-600 dark:text-emerald-400 focus:text-emerald-600 focus:bg-emerald-50 dark:focus:bg-emerald-950/30"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-sm font-medium">Concluir</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleChangeStatus(task.id, 'cancelled')}
                        className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 mt-1"
                      >
                        <XCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">Cancelar</span>
                      </DropdownMenuItem>
                    </>
                  )}
                {task.status === 'paused' &&
                  canDoOnPhase(
                    task.phase_id,
                    'can_change_status',
                    isResponsibleFor(task)
                  ) && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleChangeStatus(task.id, 'in_progress')}
                        className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-blue-600 dark:text-blue-400 focus:text-blue-600 focus:bg-blue-50 dark:focus:bg-blue-950/30"
                      >
                        <PlayCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">Retomar</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleChangeStatus(task.id, 'cancelled')}
                        className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 mt-1"
                      >
                        <XCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">Cancelar</span>
                      </DropdownMenuItem>
                    </>
                  )}
                {(task.status === 'completed' || task.status === 'cancelled') &&
                  canDoOnPhase(
                    task.phase_id,
                    'can_reopen',
                    isResponsibleFor(task)
                  ) && (
                    <DropdownMenuItem
                      onClick={() => handleChangeStatus(task.id, 'in_progress')}
                      className="flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer"
                    >
                      <RotateCcw className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">Reabrir Tarefa</span>
                    </DropdownMenuItem>
                  )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}

/* =========================================================================
 * Renderiza recursivamente uma tarefa pai com subtarefas expandidas
 * (até nível 3 — neta).
 * ========================================================================= */
function renderTaskWithSubtasks(
  task: TaskListItem,
  ctx: TaskListTableContextValue,
  depth: 0 | 1 | 2 = 0
): React.ReactNode {
  const { expandedTasks, subtasksMap } = ctx;
  const children =
    depth < 2 && expandedTasks.has(task.id) ? subtasksMap[task.id] || [] : [];

  return (
    <Fragment key={depth === 0 ? task.id : `sub-${depth}-${task.id}`}>
      <TaskRow task={task} depth={depth} />
      {children.map((child) =>
        renderTaskWithSubtasks(child, ctx, (depth + 1) as 0 | 1 | 2)
      )}
    </Fragment>
  );
}

/* =========================================================================
 * Componente público — renderiza Table completa para a lista informada
 * ========================================================================= */
export interface TaskListTableProps {
  tasks: TaskListItem[];
}

export function TaskListTable({ tasks }: TaskListTableProps) {
  const ctx = useTaskListTableContext();

  // Metadados de período buscados em lote (Retenção/Jurídico)
  const taskModule = useTaskModuleType();
  const hasPeriodColumn = taskModule === 'retention' || taskModule === 'legal';
  const { metaById } = useRetentionPeriodsMeta(
    tasks.map((t) => t.id),
    hasPeriodColumn,
  );

  return (
    <RetentionPeriodContext.Provider value={metaById}>
      <Table scrollViewport containerClassName="h-full" className="min-w-[1900px]">
        <TaskListTableHeader tasks={tasks} />
        <TableBody>
          {tasks.map((task) => renderTaskWithSubtasks(task, ctx, 0))}
        </TableBody>
      </Table>
    </RetentionPeriodContext.Provider>
  );
}
